import {
  Field,
  generateRuleList,
  IconName,
  IconVariant,
  PopoverPlacement,
  PopoverTrigger,
  SelectControl,
  Status,
  StatusSize,
  useIsFeatureEnabled
} from "@quortex/front-common"
import { useMemo } from "react"
import { Controller, useFormContext } from "react-hook-form"
import {
  switchStatusToColor,
  switchStatusToLabel
} from "@/App/play/App/Main/Pool/settings/PoolSettings/cdns/helpers"
import type { ContentSteeringServerFormType } from "@/App/play/App/Main/Settings/ContentSteering/helpers"

type Props = {
  poolList: api.ott.Pool[]
}

const TargetPoolField = ({ poolList }: Props) => {
  const form = useFormContext<ContentSteeringServerFormType>()
  const targetPoolUuid = form.watch("targetPoolUuid")

  const jitpEnabled = useIsFeatureEnabled("jitp_default_routing")

  const poolOptions = useMemo(
    () =>
      poolList.map(pool => ({
        label: pool.name,
        value: pool.uuid
      })),
    [poolList]
  )

  const switchStatus = useMemo(() => {
    const pool = poolList.find(pool => pool.uuid === targetPoolUuid)
    const status = pool?.contentSteering?.switchStatus
    if (status === "ready" && pool?.contentSteering?.dashServerAddress === "") {
      return "in_sync"
    }
    return status
  }, [poolList, targetPoolUuid])

  const isContentSteeringEnabled = useMemo(
    () => poolList.find(pool => pool.uuid === targetPoolUuid)?.contentSteering?.enabled,
    [poolList, targetPoolUuid]
  )

  return (
    <Field
      className="mt-sm"
      label="Target Pool"
      name="targetPoolUuid"
      popoverProps={{
        description: `${!jitpEnabled ? "Available for premium pools only. " : ""}Select the pool that this content steering server will connect to.`,
        icon: { name: IconName.INFO, type: IconVariant.REGULAR },
        placement: PopoverPlacement.RIGHT,
        title: "Target pool",
        trigger: PopoverTrigger.CLICK
      }}
    >
      {name => (
        <div className="flex w-full items-center gap-sm">
          <Controller
            control={form.control}
            name={name}
            render={({ field }) => (
              <SelectControl
                {...field}
                id={name}
                options={poolOptions}
                placeholder="Select pool"
                width="large"
              />
            )}
            rules={{ ...generateRuleList({ required: true }) }}
          />
          {!!switchStatus && isContentSteeringEnabled && (
            <Status color={switchStatusToColor[switchStatus]} size={StatusSize.NORMAL}>
              {switchStatusToLabel[switchStatus]}
            </Status>
          )}
        </div>
      )}
    </Field>
  )
}

export default TargetPoolField
